// Dashboard consumi LLM (Sprint 4).
// KPI mese corrente, distribuzione per modello/lingua/flow, controllo cap.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DogEarCard, RuleBicolor, AudiqMark } from '@audiq/ui';
import { api } from '../lib/api';

function fmtMoney(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

function fmtTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return (n / 1000).toFixed(1) + 'K';
  return (n / 1_000_000).toFixed(2) + 'M';
}

function pct(part: number, whole: number): number {
  if (whole === 0) return 0;
  return Math.round((part / whole) * 100);
}

function defaultYearMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function UsageDashboardPage() {
  const queryClient = useQueryClient();
  const [yearMonth, setYearMonth] = useState<string>(defaultYearMonth());
  const dash = useQuery({
    queryKey: ['usage-dashboard', yearMonth],
    queryFn: () => api.getUsageDashboard(yearMonth),
  });
  const [softLimit, setSoftLimit] = useState(1000);
  const [hardLimit, setHardLimit] = useState(5000);
  const [alertEmail, setAlertEmail] = useState('');
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (dash.data) {
      setSoftLimit(dash.data.limits.monthly_soft);
      setHardLimit(dash.data.limits.monthly_hard);
      setAlertEmail(dash.data.alert_email_recipient ?? '');
    }
  }, [dash.data]);

  const saveLimits = useMutation({
    mutationFn: () =>
      api.updateLimits({
        monthly_soft: softLimit,
        monthly_hard: hardLimit,
        alert_email_recipient: alertEmail.trim() || null,
      }),
    onSuccess: () => {
      setSavedAt(new Date());
      queryClient.invalidateQueries({ queryKey: ['usage-dashboard'] });
    },
  });

  const triggerAgg = useMutation({
    mutationFn: () => api.triggerCron('usageAggregation'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['usage-dashboard'] }),
  });

  if (dash.isLoading) {
    return (
      <p className="text-sm" style={{ color: 'var(--mute)' }}>
        Caricamento dashboard…
      </p>
    );
  }
  if (dash.isError || !dash.data) {
    return (
      <p className="text-sm" style={{ color: 'var(--danger)' }}>
        Errore caricamento dashboard.
      </p>
    );
  }

  const cur = dash.data.current;
  const prev = dash.data.previous;
  const totalEnc = cur?.total_encounters ?? 0;
  const totalCost = cur?.estimated_cost_usd ?? 0;
  const trendEnc =
    prev && prev.total_encounters > 0
      ? Math.round(((totalEnc - prev.total_encounters) / prev.total_encounters) * 100)
      : null;

  const softPct = pct(totalEnc, dash.data.limits.monthly_soft);
  const hardPct = pct(totalEnc, dash.data.limits.monthly_hard);
  const softAlerted = !!cur?.soft_limit_alert_sent_at;
  const hardReached = !!cur?.hard_limit_reached_at;

  const byModelEntries = Object.entries(dash.data.by_model).sort(
    (a, b) => b[1].tokens - a[1].tokens,
  );
  const byLangEntries = Object.entries(dash.data.by_language).sort((a, b) => b[1] - a[1]);
  const totalLang = byLangEntries.reduce((s, [, v]) => s + v, 0);
  const maxDaily = dash.data.daily_encounters.reduce((m, d) => Math.max(m, d.total), 0);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex items-start justify-between">
        <div>
          <div className="mono mb-2" style={{ color: 'var(--mute)' }}>
            Dashboard consumi · {dash.data.year_month}
          </div>
          <h2 className="serif text-3xl" style={{ color: 'var(--ink)' }}>
            Consumi LLM
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="month"
            value={yearMonth}
            onChange={(e) => setYearMonth(e.target.value)}
            className="rounded-md border px-2 py-1 text-sm"
            style={{
              background: 'var(--paper)',
              borderColor: 'var(--line-strong)',
              color: 'var(--ink)',
            }}
            aria-label="Mese"
          />
          <AudiqMark size={64} state={hardReached ? 'thinking' : 'idle'} />
        </div>
      </div>

      <RuleBicolor className="my-6" />

      {/* KPI top row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <DogEarCard
          className="rounded-md border p-4"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
            Encounter mese
          </div>
          <div className="serif mt-1 text-3xl" style={{ color: 'var(--ink)' }}>
            {totalEnc}
          </div>
          {trendEnc !== null ? (
            <div
              className="mono mt-1 text-xs"
              style={{ color: trendEnc >= 0 ? 'var(--primary)' : 'var(--danger)' }}
            >
              {trendEnc >= 0 ? '↑' : '↓'} {Math.abs(trendEnc)}% vs mese prec.
            </div>
          ) : (
            <div className="mono mt-1 text-xs" style={{ color: 'var(--mute)' }}>
              {prev ? 'mese prec. = 0' : 'no storico'}
            </div>
          )}
        </DogEarCard>

        <DogEarCard
          className="rounded-md border p-4"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
            Costo stimato
          </div>
          <div className="serif mt-1 text-3xl" style={{ color: 'var(--ink)' }}>
            {fmtMoney(totalCost)}
          </div>
          <div className="mono mt-1 text-xs" style={{ color: 'var(--mute)' }}>
            {prev ? `prev: ${fmtMoney(prev.estimated_cost_usd)}` : ''}
          </div>
        </DogEarCard>

        <DogEarCard
          className="rounded-md border p-4"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
            Modello principale
          </div>
          <div className="serif mt-1 text-base break-words" style={{ color: 'var(--ink)' }}>
            {byModelEntries[0]?.[0] ?? '—'}
          </div>
          <div className="mono mt-1 text-xs" style={{ color: 'var(--mute)' }}>
            {byModelEntries[0] ? `${byModelEntries[0][1].count} chiamate` : ''}
          </div>
        </DogEarCard>

        <DogEarCard
          className="rounded-md border p-4"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
            Lingua più frequente
          </div>
          <div className="serif mt-1 text-3xl" style={{ color: 'var(--ink)' }}>
            {byLangEntries[0]?.[0]?.toUpperCase() ?? '—'}
          </div>
          <div className="mono mt-1 text-xs" style={{ color: 'var(--mute)' }}>
            {byLangEntries[0] ? `${pct(byLangEntries[0][1], totalLang)}% del totale` : ''}
          </div>
        </DogEarCard>
      </div>

      {/* Sprint 8: Audio KPIs (visibile solo se ci sono consumi audio) */}
      {(cur?.total_audio_seconds_user ?? 0) > 0 || (cur?.estimated_cost_audio_usd ?? 0) > 0 ? (
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
          <DogEarCard
            className="rounded-md border p-4"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
              Audio user (STT)
            </div>
            <div className="serif mt-1 text-2xl" style={{ color: 'var(--ink)' }}>
              {((cur?.total_audio_seconds_user ?? 0) / 60).toFixed(1)} min
            </div>
            <div className="mono mt-1 text-xs" style={{ color: 'var(--mute)' }}>
              Deepgram
            </div>
          </DogEarCard>
          <DogEarCard
            className="rounded-md border p-4"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
              Audio bot (TTS)
            </div>
            <div className="serif mt-1 text-2xl" style={{ color: 'var(--ink)' }}>
              {((cur?.total_audio_chars_bot ?? 0) / 1000).toFixed(1)}k char
            </div>
            <div className="mono mt-1 text-xs" style={{ color: 'var(--mute)' }}>
              ElevenLabs
            </div>
          </DogEarCard>
          <DogEarCard
            className="rounded-md border p-4"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
              Costo audio
            </div>
            <div className="serif mt-1 text-2xl" style={{ color: 'var(--ink)' }}>
              {fmtMoney(cur?.estimated_cost_audio_usd ?? 0)}
            </div>
            <div className="mono mt-1 text-xs" style={{ color: 'var(--mute)' }}>
              {(
                ((cur?.estimated_cost_audio_usd ?? 0) /
                  Math.max(
                    1,
                    (cur?.estimated_cost_usd ?? 0) + (cur?.estimated_cost_audio_usd ?? 0),
                  )) *
                100
              ).toFixed(0)}
              % del totale
            </div>
          </DogEarCard>
          <DogEarCard
            className="rounded-md border p-4"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
              Encounter voce
            </div>
            <div className="serif mt-1 text-2xl" style={{ color: 'var(--ink)' }}>
              {(dash.data.encounters_by_transport?.voice ?? 0) +
                (dash.data.encounters_by_transport?.mixed ?? 0)}
            </div>
            <div className="mono mt-1 text-xs" style={{ color: 'var(--mute)' }}>
              {dash.data.encounters_by_transport?.voice ?? 0} voice ·{' '}
              {dash.data.encounters_by_transport?.mixed ?? 0} mixed
            </div>
          </DogEarCard>
        </div>
      ) : null}

      {/* Cap progress */}
      <DogEarCard
        className="mt-6 rounded-md border p-5"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
      >
        <h3 className="serif mb-3 text-xl" style={{ color: 'var(--ink)' }}>
          Stato cap consumi
        </h3>
        <div className="space-y-3">
          <div>
            <div
              className="mb-1 flex justify-between mono text-xs"
              style={{ color: 'var(--mute)' }}
            >
              <span>Cap soft (alert): {dash.data.limits.monthly_soft}</span>
              <span>{softPct}%</span>
            </div>
            <div className="h-2 w-full rounded-full" style={{ background: 'var(--soft)' }}>
              <div
                className="h-2 rounded-full transition-all"
                style={{
                  width: `${Math.min(softPct, 100)}%`,
                  background: softPct >= 100 ? 'var(--accent)' : 'var(--primary)',
                }}
              />
            </div>
            {softAlerted ? (
              <div className="mono mt-1 text-xs" style={{ color: 'var(--accent)' }}>
                ⚠ Alert email inviato il{' '}
                {new Date(cur!.soft_limit_alert_sent_at!).toLocaleString('it-IT')}
              </div>
            ) : null}
          </div>
          <div>
            <div
              className="mb-1 flex justify-between mono text-xs"
              style={{ color: 'var(--mute)' }}
            >
              <span>Cap hard (blocco): {dash.data.limits.monthly_hard}</span>
              <span>{hardPct}%</span>
            </div>
            <div className="h-2 w-full rounded-full" style={{ background: 'var(--soft)' }}>
              <div
                className="h-2 rounded-full transition-all"
                style={{
                  width: `${Math.min(hardPct, 100)}%`,
                  background: hardPct >= 100 ? 'var(--danger)' : 'var(--primary)',
                }}
              />
            </div>
            {hardReached ? (
              <div className="mono mt-1 text-xs" style={{ color: 'var(--danger)' }}>
                ⛔ Cap hard raggiunto — nuove sessioni bloccate
              </div>
            ) : null}
          </div>
        </div>

        {/* Settings inline */}
        <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="block">
            <span className="mono mb-1 block text-xs" style={{ color: 'var(--mute)' }}>
              Soft monthly
            </span>
            <input
              type="number"
              min={0}
              value={softLimit}
              onChange={(e) => setSoftLimit(Number(e.target.value))}
              className="w-full rounded-md border px-3 py-2"
              style={{
                background: 'var(--surface)',
                borderColor: 'var(--line)',
                color: 'var(--ink)',
              }}
            />
          </label>
          <label className="block">
            <span className="mono mb-1 block text-xs" style={{ color: 'var(--mute)' }}>
              Hard monthly
            </span>
            <input
              type="number"
              min={0}
              value={hardLimit}
              onChange={(e) => setHardLimit(Number(e.target.value))}
              className="w-full rounded-md border px-3 py-2"
              style={{
                background: 'var(--surface)',
                borderColor: 'var(--line)',
                color: 'var(--ink)',
              }}
            />
          </label>
          <label className="block">
            <span className="mono mb-1 block text-xs" style={{ color: 'var(--mute)' }}>
              Email alert (vuoto = admin master)
            </span>
            <input
              type="email"
              value={alertEmail}
              onChange={(e) => setAlertEmail(e.target.value)}
              className="w-full rounded-md border px-3 py-2"
              style={{
                background: 'var(--surface)',
                borderColor: 'var(--line)',
                color: 'var(--ink)',
              }}
            />
          </label>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
            {savedAt ? `salvato alle ${savedAt.toLocaleTimeString('it-IT')}` : ''}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => triggerAgg.mutate()}
              disabled={triggerAgg.isPending}
              className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
              style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
            >
              {triggerAgg.isPending ? 'aggrego…' : 'Aggrega ora'}
            </button>
            <button
              type="button"
              onClick={() => saveLimits.mutate()}
              disabled={saveLimits.isPending}
              className="rounded-md px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
              style={{ background: 'var(--primary)', color: '#fff' }}
            >
              {saveLimits.isPending ? 'salvo…' : 'Salva cap'}
            </button>
          </div>
        </div>
      </DogEarCard>

      {/* Distribuzione per modello + lingua */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <DogEarCard
          className="rounded-md border p-5"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <h3 className="serif mb-3 text-xl" style={{ color: 'var(--ink)' }}>
            Per modello
          </h3>
          {byModelEntries.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--mute)' }}>
              Nessun dato.
            </p>
          ) : (
            <ul className="space-y-2">
              {byModelEntries.map(([model, info]) => (
                <li key={model}>
                  <div className="flex justify-between text-sm">
                    <span className="mono" style={{ color: 'var(--ink)' }}>
                      {model}
                    </span>
                    <span className="mono" style={{ color: 'var(--mute)' }}>
                      {fmtTokens(info.tokens)} tokens · {info.count} chiamate
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DogEarCard>

        <DogEarCard
          className="rounded-md border p-5"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <h3 className="serif mb-3 text-xl" style={{ color: 'var(--ink)' }}>
            Per lingua
          </h3>
          {byLangEntries.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--mute)' }}>
              Nessun dato.
            </p>
          ) : (
            <ul className="space-y-2">
              {byLangEntries.map(([lang, count]) => (
                <li key={lang}>
                  <div className="flex justify-between text-sm">
                    <span className="mono" style={{ color: 'var(--ink)' }}>
                      {lang.toUpperCase()}
                    </span>
                    <span className="mono" style={{ color: 'var(--mute)' }}>
                      {count} ({pct(count, totalLang)}%)
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full" style={{ background: 'var(--soft)' }}>
                    <div
                      className="h-1.5 rounded-full"
                      style={{ width: `${pct(count, totalLang)}%`, background: 'var(--primary)' }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DogEarCard>
      </div>

      {/* Daily encounters bar chart */}
      <DogEarCard
        className="mt-6 rounded-md border p-5"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
      >
        <h3 className="serif mb-3 text-xl" style={{ color: 'var(--ink)' }}>
          Encounter per giorno
        </h3>
        {dash.data.daily_encounters.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--mute)' }}>
            Nessun dato.
          </p>
        ) : (
          <div className="flex items-end gap-1 overflow-x-auto" style={{ height: 120 }}>
            {dash.data.daily_encounters.map((d) => {
              const h = maxDaily > 0 ? Math.max(2, Math.round((d.total / maxDaily) * 100)) : 2;
              return (
                <div key={d.day} className="flex flex-col items-center gap-1" style={{ width: 24 }}>
                  <div
                    title={`${d.day}: ${d.total} (${fmtMoney(d.cost_usd)})`}
                    style={{
                      height: `${h}%`,
                      width: '100%',
                      background: 'var(--primary)',
                      borderRadius: 2,
                      minHeight: 2,
                    }}
                  />
                  <div className="mono text-[10px]" style={{ color: 'var(--mute)' }}>
                    {d.day.slice(8, 10)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DogEarCard>

      {/* Recent encounters */}
      <DogEarCard
        className="mt-6 rounded-md border p-5"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
      >
        <h3 className="serif mb-3 text-xl" style={{ color: 'var(--ink)' }}>
          Encounter recenti
        </h3>
        {dash.data.recent_encounters.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--mute)' }}>
            Nessun encounter recente.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="mono text-xs" style={{ color: 'var(--mute)' }}>
              <tr>
                <th className="text-start">Pseudonym</th>
                <th className="text-start">Stato</th>
                <th className="text-start">Lingua</th>
                <th className="text-start">Modalità</th>
                <th className="text-end">Turni</th>
                <th className="text-start">Quando</th>
              </tr>
            </thead>
            <tbody>
              {dash.data.recent_encounters.map((e) => (
                <tr key={e.id} className="border-t" style={{ borderColor: 'var(--line)' }}>
                  <td className="py-1.5 mono" style={{ color: 'var(--primary)' }}>
                    {e.pseudonym}
                  </td>
                  <td style={{ color: 'var(--mute)' }}>{e.status}</td>
                  <td className="mono" style={{ color: 'var(--ink)' }}>
                    {e.language_detected?.toUpperCase() ?? '—'}
                  </td>
                  <td className="mono text-xs" style={{ color: 'var(--mute)' }}>
                    {e.transport_mode === 'voice'
                      ? '🎤 voice'
                      : e.transport_mode === 'mixed'
                        ? '💬🎤 mixed'
                        : '💬 text'}
                  </td>
                  <td className="text-end mono" style={{ color: 'var(--ink)' }}>
                    {e.total_turns}
                  </td>
                  <td className="mono text-xs" style={{ color: 'var(--mute)' }}>
                    {new Date(e.started_at).toLocaleString('it-IT')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DogEarCard>
    </div>
  );
}
